// Multi-select inspector facade (#40): mirrors DynamicElement's read/write
// surface over N members. Node-requirable (store coupling lazy + guarded).

// NOT a live sentinel — that would parseInt→NaNpx across tools. `undefined` +
// a `mixed` marker instead: tools tolerate undefined, marker drives "Mixed".
const MIXED_LEAF = Object.freeze({ r: undefined, c: undefined, mixed: true });

const COMPOSITE_PREFIX = "multi::";

function deepEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") {
    return false;
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every(k => deepEqual(a[k], b[k]));
}

function intersectLeaf(members, readLeaf) {
  let firstLeaf;
  let firstSet = false;
  for (const member of members) {
    const leaf = readLeaf(member);
    const raw = leaf?.r;
    if (!firstSet) {
      firstLeaf = leaf;
      firstSet = true;
      continue;
    }
    if (!deepEqual(raw, firstLeaf?.r)) {
      return MIXED_LEAF;
    }
  }
  return firstSet ? firstLeaf : undefined;
}

function bareId(qualifiedId) {
  return String(qualifiedId).split(":")[0];
}

class MultiDynamicElement {
  static COMPOSITE_PREFIX = COMPOSITE_PREFIX;

  static MIXED_LEAF = MIXED_LEAF;

  #members;

  #compositeId;

  // Sorted so the id (and the watcher keyed on it) is invariant to click order.
  static formatCompositeId(qualifiedIds) {
    const bare = qualifiedIds.map(bareId);
    return `${COMPOSITE_PREFIX}${[...bare].sort().join("|")}`;
  }

  static isCompositeId(id) {
    return typeof id === "string" && id.startsWith(COMPOSITE_PREFIX);
  }

  static parseCompositeId(id) {
    if (!MultiDynamicElement.isCompositeId(id)) {
      return [];
    }
    return id.slice(COMPOSITE_PREFIX.length).split("|").filter(Boolean);
  }

  constructor(compositeId, members) {
    this.#compositeId = compositeId;
    this.#members = members;
  }

  get id() {
    return this.#compositeId;
  }

  get members() {
    return this.#members;
  }

  // Shape-only reads: the option intersection guarantees a compatible set.
  get element() {
    return this.#members[0]?.element ?? null;
  }

  get type() {
    return this.#members[0]?.type;
  }

  get elementDef() {
    return this.#members[0]?.elementDef;
  }

  get parsedOptions() {
    return this.#members[0]?.parsedOptions;
  }

  get isDestination() {
    return this.#members.every(m => m.isDestination);
  }

  // Theme/branding members are filtered out at build time (§ DynamicElement.get),
  // so a composite is never a theme element.
  get isTheme() {
    return false;
  }

  get hasAnyOverrides() {
    return this.#members.some(m => m.hasAnyOverrides);
  }

  /** ***************** Reads (intersect across members) ***************** */
  getRawOptionValue(optionKey, breakpoint, state) {
    return intersectLeaf(this.#members, m => m.getRawOptionValue(optionKey, breakpoint, state));
  }

  getOptionValuesBundle(optionKeys, breakpoint, state) {
    return optionKeys.reduce((bundle, key) => {
      bundle[key] = this.getRawOptionValue(key, breakpoint, state);
      return bundle;
    }, {});
  }

  getOptionValue(optionKey, breakpoint, state, context) {
    let first;
    let firstSet = false;
    for (const member of this.#members) {
      const value = member.getOptionValue(optionKey, breakpoint, state, context);
      if (!firstSet) {
        first = value;
        firstSet = true;
        continue;
      }
      if (!deepEqual(value, first)) {
        return undefined;
      }
    }
    return firstSet ? first : undefined;
  }

  // Override probes OR across members (any override shows the badge); inherit
  // probes AND (a mixed override state is not "inheriting").
  isOptionOverridden(optionKey, breakpoint = "default", state = "default") {
    return this.#members.some(m => m.isOptionOverridden(optionKey, breakpoint, state));
  }

  determineIfOptionsAreOverwritten(optionKeys, breakpoint, state) {
    return this.#members.some(m => m.determineIfOptionsAreOverwritten(optionKeys, breakpoint, state));
  }

  determineIfOptionsAreInheriting(optionKeys, breakpoint, state) {
    return this.#members.every(m => m.determineIfOptionsAreInheriting(optionKeys, breakpoint, state));
  }

  optionCanBeInherited(optionKey, breakpoint, state) {
    return this.#members.every(m => m.optionCanBeInherited(optionKey, breakpoint, state));
  }

  hasBrandingFallback(optionKey, breakpoint = "default", state = "default", themeMode = "light-mode") {
    return this.#members.every(m => m.hasBrandingFallback(optionKey, breakpoint, state, themeMode));
  }

  updateOption(optionKey, breakpoint, state, rawOptionValue, context) {
    this.#withBulkUndoGroup(() => {
      this.#members.forEach(member => {
        member.updateOption(optionKey, breakpoint, state, rawOptionValue, context);
      });
    });
  }

  // Coalesces the N members' debounced saves into one undo unit. Guarded so a
  // test env with no booted store still runs the fan-out.
  #withBulkUndoGroup(fn) {
    let store = null;
    try {
      // eslint-disable-next-line global-require
      store = require("%/dynamic-component-editor/stores/dynamic-component-editor").default();
    } catch {
      store = null;
    }
    if (store && typeof store.beginBulkOptionEdit === "function") {
      store.beginBulkOptionEdit();
    }
    fn();
  }
}

module.exports = MultiDynamicElement;
module.exports.default = MultiDynamicElement;
module.exports.MIXED_LEAF = MIXED_LEAF;
module.exports.COMPOSITE_PREFIX = COMPOSITE_PREFIX;
module.exports.intersectLeaf = intersectLeaf;
module.exports.deepEqual = deepEqual;
